import styled, { css } from 'styled-components';
import { sm } from '@exonet/react-shared/lib/styled';

/**
 * Wraps a DataManager and its skeleton placeholder.
 *
 * The DataManager renders only its built-in spinner (a plain <div>, not
 * `.data-manager`) during the initial mount, then swaps to its real content
 * (`.data-manager`). This is driven purely by CSS — no loading state needed:
 *  - while only the spinner is present, hide it and show the skeleton;
 *  - once `.data-manager` has mounted, hide the skeleton.
 */
export const Boundary = styled.div`
  /* Hide the DataManager's spinner (any direct div that is neither the
     skeleton fallback nor the mounted data-manager content). */
  > div:not(.data-manager):not(.data-manager-skeleton) {
    display: none;
  }

  /* Once the real content has mounted, hide the skeleton fallback. */
  &:has(.data-manager) > .data-manager-skeleton {
    display: none;
  }
`;

// Plain block flow, like the real `.data-manager` root: a flex container here
// would stop the children's vertical margins from collapsing, making the
// skeleton spacing larger than the loaded page (e.g. header 8px + details 15px
// would add up to 23px instead of collapsing to 15px).
export const Fallback = styled.div.attrs({ className: 'data-manager-skeleton' })``;

// The white card that holds the controls + table skeleton. Carries the real
// `data-manager-content` class — the global card styling (padding, background,
// radius, table rules) is scoped to include `.data-manager-skeleton` in
// Core/styled/base.js, so the skeleton card can never diverge from the real one.
export const SkeletonCard = styled.div.attrs({ className: 'data-manager-content' })``;

// The controls section: pinned to the same min-height as the real
// .data-manager-controls (see base.js), so per-control height differences can
// never shift the table. Content sits at the top, like the real section.
export const SkeletonControlsSection = styled.div`
  min-height: var(--data-manager-controls-min-height, 58px);
  margin-bottom: 8px;
`;

// The actual controls row inside the section. Items stretch to the tallest
// sibling (like the real FilterContent row), so the Actions group can align
// its content with the bottom of the filter selects. Hidden below the sm
// breakpoint: the real filters/search are collapsed behind the mobile
// "Filters" toggle there (the skeleton shows that toggle instead).
export const SkeletonControlsRow = styled.div`
  display: none;

  ${sm(css`
    display: flex;
    align-items: stretch;
    /* Match the 5px gap the real FilterSelect uses between filters. */
    gap: 5px;
  `)}
`;

// Right-aligned group holding the export button + search placeholder. Its
// content aligns to the bottom of the (stretched) row, exactly like the real
// ControlSection Actions (whose children use align-self: flex-end) — so the
// search lines up with the selects when filters are present, and sits at the
// top when there are none.
export const SkeletonActions = styled.div`
  display: flex;
  align-items: flex-end;
  gap: var(--wa-space-s, 8px);
  margin-left: auto;
`;

// A form-input placeholder box built from the same metrics as the real input
// (padding + line-height + border via the same CSS vars), so its rendered
// height matches a real input by construction. The zero-width space gives it
// the natural text line; the skeleton bar fills the box absolutely.
export const SkeletonInputBox = styled.div`
  position: relative;
  width: 100%;
  padding: var(--form-input-padding, var(--xs-padding));
  line-height: var(--line-height);
  border: var(--form-input-border-width, 1px) solid transparent;
`;

// A filter placeholder: the (real) filter label above a skeleton input,
// mirroring FilterSelect's label/select layout (block, like the real filter).
export const LabeledField = styled.div`
  display: flex;
  flex-direction: column;
`;

// Matches FilterSelect's label styling so the name reads identically.
export const FieldLabel = styled.div`
  font-weight: bold;
  text-transform: uppercase;
  font-size: 65%;
`;

// A non-interactive placeholder that looks like a (disabled) button and shows
// the real, static button label — for controls that don't depend on the data.
export const SkeletonButton = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 5rem;
  height: var(--wa-form-control-height, 2.25rem);
  padding: 0 12px;
  border: 1px solid var(--bg-3);
  border-radius: var(--border-radius);
  color: var(--text);
  opacity: 0.55;
  white-space: nowrap;
  user-select: none;
  ${({ $end }) => $end && 'margin-left: auto;'}
`;
